// 🎥 Arab Sanad TV — public video gallery.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { attachmentUrl } from "@/lib/community";
import BackBar from "@/components/BackBar";
import { Tv, Search, Heart, Share2, Bookmark, Play, ArrowLeft } from "lucide-react";

const CATEGORIES = [
  { slug: "",                ar: "الكل",          en: "All" },
  { slug: "homepage",        ar: "الرئيسية",     en: "Homepage" },
  { slug: "resume_center",   ar: "السيرة الذاتية", en: "Resume Center" },
  { slug: "jobs",            ar: "الوظائف",      en: "Jobs" },
  { slug: "housing",         ar: "السكن",        en: "Housing" },
  { slug: "discover",        ar: "اكتشف أمريكا",  en: "Discover" },
  { slug: "tutorials",       ar: "شروحات",       en: "Tutorials" },
  { slug: "marketing",       ar: "إعلانات",      en: "Marketing" },
];

const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });

// Convert YouTube/Vimeo URL to embeddable iframe URL.
const embedUrl = (url) => {
  if (!url) return "";
  const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([\w-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vmMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vmMatch) return `https://player.vimeo.com/video/${vmMatch[1]}`;
  return url;  // raw object-storage URL or other
};

export default function ArabSanadTV() {
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [cat, setCat]       = useState("");
  const [q, setQ]           = useState("");
  const [videos, setVideos] = useState([]);
  const [active, setActive] = useState(null);   // currently playing video

  useEffect(() => {
    axios.get(`${API}/videos${cat ? `?category=${cat}` : ""}`).then((r) => setVideos(r.data || [])).catch(() => {});
  }, [cat]);

  const filtered = videos.filter((v) => !q.trim() || (v.title + " " + (v.description || "")).toLowerCase().includes(q.toLowerCase()));

  const recordEvent = (video_id, event) => {
    if (!token) return;
    axios.post(`${API}/videos/${video_id}/event`, { event }, auth(token)).catch(() => {});
  };
  const like = async (v) => {
    if (!token) { navigate("/login"); return; }
    const r = await axios.post(`${API}/videos/${v.video_id}/like`, {}, auth(token)).then((x) => x.data);
    setVideos((xs) => xs.map((x) => x.video_id === v.video_id ? { ...x, likes_count: r.likes } : x));
    toast.success(r.liked ? t("أحببت الفيديو", "Liked") : t("أزلت الإعجاب", "Unliked"));
  };
  const save = async (v) => {
    if (!token) { navigate("/login"); return; }
    const r = await axios.post(`${API}/videos/${v.video_id}/save`, {}, auth(token)).then((x) => x.data);
    toast.success(r.saved ? t("تم الحفظ", "Saved") : t("تمت الإزالة", "Removed"));
  };
  const share = async (v) => {
    const url = `${window.location.origin}/tv?v=${v.video_id}`;
    try { await navigator.clipboard.writeText(url); toast.success(t("تم نسخ الرابط", "Link copied")); recordEvent(v.video_id, "share"); } catch {}
  };

  return (
    <DashboardLayout title={t("Arab Sanad TV", "Arab Sanad TV")}>
      <BackBar to="/dashboard" label_ar="الرئيسية" label_en="Home"
        crumbs={[{ to: "/dashboard", ar: "الرئيسية", en: "Home" }, { ar: "Arab Sanad TV", en: "Arab Sanad TV" }]} />
      <div className="space-y-4" data-testid="tv-page">
        {/* Hero */}
        <div className="glass-card rounded-3xl p-5 lg:p-7 bg-luxury">
          <div className="flex items-center gap-3">
            <Tv className="text-yellow-400" size={28} />
            <div>
              <h2 className="text-2xl lg:text-4xl font-bold text-luxury">🎥 Arab Sanad TV</h2>
              <p className="text-slate-300 text-sm mt-1">{t("شروحات وفيديوهات تساعدك في حياتك في أمريكا.", "Tutorials & videos that help you thrive in America.")}</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center" data-testid="tv-filters">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute right-3 top-3 text-slate-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("ابحث في الفيديوهات…", "Search videos…")} className="bg-slate-900 text-white border-slate-700 pr-9" data-testid="tv-search" />
          </div>
          <div className="flex gap-1 overflow-x-auto">
            {CATEGORIES.map((c) => (
              <button key={c.slug || "all"} onClick={() => setCat(c.slug)}
                className={`text-xs px-3 py-1.5 rounded-full whitespace-nowrap transition ${cat === c.slug ? "bg-yellow-500 text-black font-semibold" : "bg-slate-900 text-slate-300 hover:bg-slate-800"}`}
                data-testid={`tv-cat-${c.slug || 'all'}`}>
                {language === "ar" ? c.ar : c.en}
              </button>
            ))}
          </div>
        </div>

        {/* Active player */}
        {active && (
          <div className="glass-card rounded-2xl p-3" data-testid="tv-player">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-bold text-white">{active.title}</h3>
              <button onClick={() => setActive(null)} className="text-slate-400 hover:text-white" data-testid="tv-close-btn"><ArrowLeft size={16} /></button>
            </div>
            <div className="aspect-video rounded-xl overflow-hidden bg-black">
              {active.source_type === "upload"
                ? <video controls autoPlay className="w-full h-full" src={attachmentUrl(active.source_url)} onPlay={() => recordEvent(active.video_id, "view")} />
                : <iframe src={embedUrl(active.source_url)} className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen onLoad={() => recordEvent(active.video_id, "view")} title={active.title} />}
            </div>
            <p className="text-sm text-slate-300 mt-2">{active.description}</p>
            <div className="flex gap-2 mt-2">
              <Button size="sm" onClick={() => like(active)}  className="btn-primary"            data-testid="tv-like-btn"><Heart size={12} className="ml-1" />{active.likes_count || 0}</Button>
              <Button size="sm" onClick={() => save(active)}  variant="outline" className="border-slate-700 text-slate-300" data-testid="tv-save-btn"><Bookmark size={12} className="ml-1" />{t("احفظ", "Save")}</Button>
              <Button size="sm" onClick={() => share(active)} variant="outline" className="border-slate-700 text-slate-300" data-testid="tv-share-btn"><Share2 size={12} className="ml-1" />{t("مشاركة", "Share")}</Button>
            </div>
          </div>
        )}

        {/* Grid */}
        {filtered.length === 0 ? (
          <div className="glass-card rounded-2xl p-10 text-center text-slate-400" data-testid="tv-empty">
            <Tv className="mx-auto text-slate-600 mb-2" size={32} />
            <p className="text-sm">{t("لا توجد فيديوهات منشورة بعد. تابعنا قريباً!", "No published videos yet. Stay tuned!")}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="tv-grid">
            {filtered.map((v) => (
              <button key={v.video_id} onClick={() => { setActive(v); recordEvent(v.video_id, "click"); }}
                className="group text-right bg-slate-900/60 hover:bg-slate-900 border border-slate-800 hover:border-yellow-500/40 rounded-2xl overflow-hidden transition-all" data-testid={`tv-card-${v.video_id}`}>
                <div className="relative aspect-video bg-black">
                  {v.thumbnail_url
                    ? <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" onError={(e) => e.target.style.display="none"} />
                    : <div className="w-full h-full flex items-center justify-center text-slate-600"><Tv size={32} /></div>}
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition flex items-center justify-center">
                    <Play className="text-white opacity-80 group-hover:opacity-100" size={36} />
                  </div>
                </div>
                <div className="p-3">
                  <div className="text-sm font-semibold text-white line-clamp-2 min-h-[2.5em]">{v.title}</div>
                  <div className="text-[10px] text-slate-500 mt-1 flex justify-between">
                    <span>{(v.views || 0).toLocaleString()} {t("مشاهدة", "views")}</span>
                    <Badge className="bg-slate-800 text-slate-300 text-[10px]">{v.category}</Badge>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
